/**
 * The one place the run's typed-array layout is defined (docs/models README §7.1). A run mutates these arrays in place month by
 * month and materializes `BankState` / `MarketState` only where the API needs one (run-state.ts).
 *
 * - Vintage columns: one typed array per column per book, row = vintage slot; `NaN` = the optional Vintage field is absent.
 * - P&L accumulator: one Float64Array per seat, reset at each month start; the month's P&L by line as the ledger posts it.
 * - Market path: one Float64Array[month × field] per run: env (01.1), every product's reference rate (01.2; deposits = the
 *   01.3 lag state), shared by every seat.
 */
import { AFS_BOOKS, BookId, DEPOSIT_BOOKS, HTM_BOOKS, LOAN_BOOKS, type EnvSnapshot } from "@bsim/domain";
import type { PnlSummary } from "../kpis";

/**
 * Vintage columns (Float64Array). The first group mirrors the Vintage fields the run moves and writes back at materialization; the
 * second is read-only after the slot opens (copied for the kernels); the third is per-month scratch the loan kernel owns; the securities fields (05.3) a deposit vintage's uninsured share (02.4; NaN = the product's SimStart share) and the pledged amount (06.2) come last.
 */
export const VINTAGE_COLUMNS = [
  "balanceUsd", "couponRate", "targetRate", "fairValueUsd", "nextResetMonth", "prepayAnchorRate",
  "maturityMonth", "originMonth", "ageAtSimStartMonths", "spreadBp", "underwritingBox",
  "openingUsd", "runoffUsd", "lossAtDefaultUsd",
  "wacCouponRate", "durationYears", "convexity",
  "pledgedUsd", "uninsuredRate",
] as const;
export type VintageColumn = (typeof VINTAGE_COLUMNS)[number];
/**
 * Integer vintage columns (Int32Array, so kernels index by them directly): the product's index in the archetype list, hot money 0/1, a securities
 * vintage's market-yield field in the market path (`yieldField`, 01.2) and a deposit front lot's graduation month (`lot`: 0 = not a live front lot,
 * −1 = a graduated lot's empty shell, dropped at the next month start; 02.5).
 */
export const VINTAGE_INDEX_COLUMNS = ["productIndex", "hot", "yieldField", "lot"] as const;
export type VintageIndexColumn = (typeof VINTAGE_INDEX_COLUMNS)[number];

/** Columns written back onto the Vintage at materialization (the rest are the vintage's own fields or scratch). */
export const WRITTEN_BACK = [
  "balanceUsd", "couponRate", "targetRate", "fairValueUsd", "nextResetMonth", "prepayAnchorRate", "maturityMonth", "wacCouponRate", "durationYears", "convexity",
  "pledgedUsd", "uninsuredRate",
] as const satisfies readonly VintageColumn[];

/** Book index = position in cp1 `BookId.options`; books are arrays in that order. */
export const BOOKS: readonly BookId[] = BookId.options;
const BOOK_INDEX: ReadonlyMap<string, number> = new Map(BOOKS.map((b, i) => [b, i]));
/** A book's index in `BOOKS` (−1: not a book). */
export const bookIndex = (book: BookId): number => BOOK_INDEX.get(book) ?? -1;
export const LOAN_BOOK_INDEXES: readonly number[] = LOAN_BOOKS.map(bookIndex);
export const AFS_BOOK_INDEXES: readonly number[] = AFS_BOOKS.map(bookIndex);
export const SECURITIES_BOOK_INDEXES: readonly number[] = [...AFS_BOOKS, ...HTM_BOOKS].map(bookIndex);
export const DEPOSIT_BOOK_INDEXES: readonly number[] = DEPOSIT_BOOKS.map(bookIndex);

/**
 * The month's P&L by line (the same classes `summarizePostings` reads off posted accounts), plus interest expense per deposit
 * book at `PNL_DEPOSIT_INTEREST + k` for `DEPOSIT_BOOKS[k]`.
 */
export const PNL = {
  interestIncome: 0, interestExpense: 1, fees: 2, opex: 3, cdi: 4, tax: 5, income: 6, expense: 7,
  netChargeOffs: 8, provision: 9, aclChange: 10, dividends: 11, buybacks: 12, issuance: 13, swapInterest: 14, oneOffOpex: 15, lapsingOverride: 16,
} as const;
export const PNL_DEPOSIT_INTEREST = 17;
export const PNL_FIELDS = PNL_DEPOSIT_INTEREST + DEPOSIT_BOOKS.length;

/** The month's P&L so far as the PnlSummary `summarizePostings` would read off its postings. */
export function pnlSummary(pnl: Float64Array): PnlSummary {
  const depositInterestUsd: Partial<Record<BookId, number>> = {};
  DEPOSIT_BOOK_INDEXES.forEach((b, k) => { const x = pnl[PNL_DEPOSIT_INTEREST + k] as number; if (x !== 0) depositInterestUsd[BOOKS[b] as BookId] = x; });
  const income = pnl[PNL.income] as number, expense = pnl[PNL.expense] as number, tax = pnl[PNL.tax] as number;
  return {
    niiUsd: (pnl[PNL.interestIncome] as number) - (pnl[PNL.interestExpense] as number), swapInterestUsd: pnl[PNL.swapInterest] as number, oneOffOpexUsd: pnl[PNL.oneOffOpex] as number, lapsingOverrideUsd: pnl[PNL.lapsingOverride] as number, feesUsd: pnl[PNL.fees] as number, opexUsd: pnl[PNL.opex] as number,
    cdiAmortizationUsd: pnl[PNL.cdi] as number, taxUsd: tax, netChargeOffsUsd: pnl[PNL.netChargeOffs] as number, provisionUsd: pnl[PNL.provision] as number, dividendsUsd: pnl[PNL.dividends] as number,
    pretaxIncomeUsd: income - expense + tax, netIncomeUsd: income - expense, depositInterestUsd,
  };
}

/** Pre-tax income of the month so far (07.5 E1). */
export const pretaxIncomeUsd = (pnl: Float64Array): number => (pnl[PNL.income] as number) - (pnl[PNL.expense] as number) + (pnl[PNL.tax] as number);

/** Scalar env fields of the market path, in `EnvSnapshot` names; the curve points follow them. */
export const ENV_SCALARS = [
  "ffRate", "sofrRate", "cpRate", "mortgageRate", "mbsYieldRate", "gdpGrowthRate", "cpiRate", "unemploymentRate",
  "hpiIndex", "creIndex", "officeIndex", "officeVacancyRate",
] as const satisfies readonly (keyof EnvSnapshot)[];
export const CURVE_POINTS = ["3m", "2y", "5y", "10y"] as const;
/** Path row width: env scalars, curve points, then one reference rate per product (`ArchetypeInputs.products` order). */
export const envFieldCount = ENV_SCALARS.length + CURVE_POINTS.length;
export const pathWidth = (productCount: number): number => envFieldCount + productCount;

/** a[i] += x on a run array (indexes come from the layout, so always in range). */
export function addAt(a: Float64Array, i: number, x: number): void {
  a[i] = (a[i] as number) + x;
}
